import React from "react";
import { Link } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Info } from "@gaming/xds-react-js-icons";

import { xboxOnboardSiteLocationPrefix } from "../../../../constants";
import { useString, useStrings } from "../../../../localization/localizationHelpers";
import * as styles from "../../PartnerHomePage.module.css";

type PartnerLifecycleBannersProps = Readonly<{
  showGameConceptRequired: boolean;
  showGameConceptReview: boolean;
  showAdditionalContracts: boolean;
  showApplyToIdXbox: boolean;
  hasStartedIdXboxApplication: boolean;
  canSubmitIdApplication: boolean;
  additionalContractsMessageKey: string;
}>;

const StatusBanner = ({ title, message }: Readonly<{ title: string; message: string }>) => (
  <div className={styles.additionalContractsBanner} role="status">
    <div className={styles.additionalContractsHeader}>
      <span className={styles.statusDotYellow} aria-hidden="true" />
      <h3 className={styles.additionalContractsTitle}>{title}</h3>
    </div>
    <p className={styles.additionalContractsDescription}>{message}</p>
  </div>
);

export const PartnerLifecycleBanners = ({
  showGameConceptRequired,
  showGameConceptReview,
  showAdditionalContracts,
  showApplyToIdXbox,
  hasStartedIdXboxApplication,
  canSubmitIdApplication,
  additionalContractsMessageKey,
}: PartnerLifecycleBannersProps) => {
  const [
    applyIdXboxTitle,
    continueIdXboxApplicationTitle,
    additionalContractsTitle,
    gameConceptRequiredTitle,
    gameConceptRequiredMessage,
    gameConceptReviewTitle,
    gameConceptReviewMessage,
  ] = useStrings(
    "partnerHomeApplyIdXboxTitle",
    "partnerHomeContinueIdXboxApplicationTitle",
    "infoBoxAdditionalAgreementsTitle",
    "partnerHomeGameConceptRequiredTitle",
    "partnerHomeGameConceptRequiredMessage",
    "partnerHomeGameConceptReviewTitle",
    "partnerHomeGameConceptReviewMessage"
  );
  const [applyIdXboxDescription, additionalContractsMessage] = useStrings(
    "partnerHomeApplyIdXboxDescription",
    additionalContractsMessageKey
  );
  const administratorGuidance = useString("idXboxApplicationAdministratorRequired");
  const idXboxBannerClassName = `${styles.applyIdXboxBanner} ${
    hasStartedIdXboxApplication ? styles.continueIdXboxBanner : ""
  }`;
  const idXboxBannerContent = (
    <>
      <div className={styles.applyIdXboxContent}>
        <div className={styles.applyIdXboxHeader}>
          <span
            aria-hidden="true"
            className={hasStartedIdXboxApplication ? styles.statusDotGreen : styles.statusDotYellow}
          />
          <h3 className={styles.applyIdXboxTitle}>
            {hasStartedIdXboxApplication ? continueIdXboxApplicationTitle : applyIdXboxTitle}
          </h3>
        </div>
        {!hasStartedIdXboxApplication && (
          <p className={styles.applyIdXboxDescription}>{applyIdXboxDescription}</p>
        )}
        {!canSubmitIdApplication && (
          <div className={styles.administratorGuidance} role="status">
            <Icon aria-hidden="true" icon={Info} iconSize={IconSize.Custom} />
            <span>{administratorGuidance}</span>
          </div>
        )}
      </div>
      <span className={styles.actionItemChevron} aria-hidden="true">
        &rsaquo;
      </span>
    </>
  );

  return (
    <>
      {showGameConceptRequired && (
        <StatusBanner title={gameConceptRequiredTitle} message={gameConceptRequiredMessage} />
      )}
      {showGameConceptReview && (
        <StatusBanner title={gameConceptReviewTitle} message={gameConceptReviewMessage} />
      )}
      {showAdditionalContracts && (
        <StatusBanner title={additionalContractsTitle} message={additionalContractsMessage} />
      )}
      {showApplyToIdXbox &&
        (canSubmitIdApplication ? (
          <Link className={idXboxBannerClassName} to={`${xboxOnboardSiteLocationPrefix}/id`}>
            {idXboxBannerContent}
          </Link>
        ) : (
          <div aria-disabled="true" className={idXboxBannerClassName} role="link">
            {idXboxBannerContent}
          </div>
        ))}
    </>
  );
};
